/**
 * Einstiegspunkt.
 *
 * Reihenfolge ist wesentlich: Erst die Laufzeitkonfiguration laden, dann
 * Amplify damit einrichten, erst danach etwas rendern, das sich anmelden will.
 * Der Vorgänger konfigurierte Amplify beim Import mit Werten aus einer
 * eingecheckten Datei — dieselbe Anwendung konnte dadurch nur gegen genau eine
 * Umgebung laufen.
 */

import { CssBaseline, ThemeProvider } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { getCurrentUser } from "aws-amplify/auth";
import { StrictMode, useState } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import { App } from "./App";
import { Anmeldung } from "./auth/Anmeldung";
import { konfiguriereAuth } from "./auth/amplify";
import { ladeConfig } from "./config";
import { Startfehler } from "./Startfehler";
import { theme } from "./theme";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Beim Fensterwechsel neu zu laden ist hier nur Lärm: Die Daten ändert
      // ausschliesslich diese eine Anwendung.
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000,
    },
  },
});

function Wurzel({ angemeldet }: { angemeldet: boolean }) {
  const [istAngemeldet, setIstAngemeldet] = useState(angemeldet);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        {istAngemeldet ? (
          <BrowserRouter>
            <App
              onAbgemeldet={() => {
                queryClient.clear();
                setIstAngemeldet(false);
              }}
            />
          </BrowserRouter>
        ) : (
          <Anmeldung onFertig={() => setIstAngemeldet(true)} />
        )}
      </QueryClientProvider>
    </ThemeProvider>
  );
}

async function starten() {
  const wurzel = createRoot(document.getElementById("root") as HTMLElement);
  try {
    konfiguriereAuth(await ladeConfig());
  } catch (fehler) {
    // Ohne Konfiguration gibt es nichts zu zeigen -- aber eine leere Seite
    // erklärt nichts. Also die Ursache anzeigen.
    wurzel.render(<Startfehler fehler={fehler} />);
    return;
  }

  const angemeldet = await getCurrentUser().then(
    () => true,
    () => false,
  );

  wurzel.render(
    <StrictMode>
      <Wurzel angemeldet={angemeldet} />
    </StrictMode>,
  );
}

void starten();
